import { useLocation, useNavigate } from 'react-router-dom';

import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';

import { useAuthorization } from '@/shared/hooks/useAuthorization';
import { useMenuPermissions } from '@/shared/hooks/useMenuPermissions';

import styles from './Sidebar.module.css';
import { adminSettingsMenu, sidebarMenus } from './sidebarMenus';

const { Sider } = Layout;

type SidebarProps = {
  colorBgContainer: string;
  collapsed?: boolean;
};

export const Sidebar: React.FC<SidebarProps> = ({ colorBgContainer, collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyRole } = useAuthorization();
  const { hasRoutePermission } = useMenuPermissions();

  const canAccessAdmin = hasAnyRole(['admin', 'manager']) && hasRoutePermission('/admin');

  const filteredSidebarMenus = sidebarMenus.filter((item) => hasRoutePermission(item.path));

  const mainMenuItems: MenuProps['items'] = filteredSidebarMenus.map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
  }));

  const adminMenuItem: MenuProps['items'] = [
    {
      key: adminSettingsMenu.key,
      label: adminSettingsMenu.label,
      icon: adminSettingsMenu.icon,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    const mainMenuItem = filteredSidebarMenus.find((item) => item.key === key);
    if (mainMenuItem) {
      const currentPath = location.pathname;
      const targetPath = mainMenuItem.path;

      if (targetPath === '/') {
        if (currentPath === '/') {
          return;
        }
      } else {
        const isWorkbench = currentPath.includes('/workbench/');

        if (!isWorkbench && (currentPath === targetPath || currentPath.startsWith(targetPath + '/'))) {
          return;
        }
      }

      navigate(mainMenuItem.path);
      return;
    }

    if (key === adminSettingsMenu.key) {
      if (location.pathname === adminSettingsMenu.path) {
        return;
      }
      navigate(adminSettingsMenu.path);
    }
  };

  const getSelectedKeys = (): string[] => {
    const currentPath = location.pathname;

    const exactMainMatch = filteredSidebarMenus.find((item) => item.path === currentPath);
    if (exactMainMatch) {
      return [exactMainMatch.key];
    }

    if (currentPath === adminSettingsMenu.path || currentPath.startsWith('/admin')) {
      return [adminSettingsMenu.key];
    }

    const partialMainMatch = filteredSidebarMenus.find((item) => {
      if (item.path === '/') return currentPath === '/';
      return currentPath.startsWith(item.path);
    });
    if (partialMainMatch) {
      return [partialMainMatch.key];
    }

    return [];
  };

  const selectedKeys = getSelectedKeys();

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={240}
      collapsedWidth={64}
      className={styles.sidebar}
      style={{ background: colorBgContainer }}>
      <div className={styles.mainMenuContainer}>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={mainMenuItems}
          onClick={handleMenuClick}
          className={styles.sidebarMenu}
          style={{ background: colorBgContainer }}
          inlineCollapsed={collapsed}
        />
      </div>

      {canAccessAdmin && (
        <div className={styles.adminMenuContainer}>
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            items={adminMenuItem}
            onClick={handleMenuClick}
            className={styles.adminMenu}
            style={{ background: colorBgContainer }}
            inlineCollapsed={collapsed}
          />
        </div>
      )}
    </Sider>
  );
};
